// Manifest der Tab-Typen (docs/shell.md).
//
// Eine Datei, die alle Typen kennt -- analog zu routes.ts. Die HUELLE
// kennt sie nicht: sie fragt nur die Registry. Damit bleibt die Shell
// von jeder Domaene unabhaengig, und eine neue Domaene ist ein Eintrag
// hier plus ein Panel, kein Eingriff im Rahmen.
//
// Die Komponenten werden lazy geladen: ein Panel, das nie geoeffnet
// wird, kostet nichts im Bundle.
//
// Beim Anlegen eines neuen Typs:
//   - params sind die IDENTITAET des Dings (Pfad, hostId+name, …).
//     Gleiche Identitaet = gleicher Tab, siehe tabId().
//   - title bekommt genau diese params und macht Klartext daraus.
//   - Das Panel traegt KEINEN eigenen Rahmen (kein fixed inset-0,
//     kein Backdrop, kein Schliessen-Knopf) -- das ist Sache der Huelle.

import { repoName } from "../claude/claude";
import { claudeChat, getClaude } from "../claude-summary.svelte";
import type { IconName } from "../Icon.svelte";
import { sectionForPath } from "../nav-sections";
import { findRoute, tabWurzelFuer } from "../routes";
import { registerPanel } from "./registry";

let registriert = false;

/** Icon einer Seite = Icon ihrer Sektion (nav-sections.ts). So traegt
 *  die Tab-Leiste dieselbe Ordnung wie die Aktivitaetsleiste daneben. */
export function seitenIcon(path: string): IconName {
  if (path === "/") return "home";
  return sectionForPath(path)?.icon ?? "file";
}

/** Titel einer Seite. Bei Param-Routen (/infra/cluster/forge) ist das
 *  Route-Label nur die Gattung ("Host") -- der Tab soll aber das DING
 *  benennen, sonst heissen zwei offene Hosts gleich. */
export function seitenTitel(path: string): string {
  // Ein Register traegt den Namen SEINER SEITE: der Tab heisst
  // "Storage", auch wenn gerade der Reiter "Alarme" offen ist -- was
  // darin gewaehlt ist, sagt der Breadcrumb.
  const wurzel = tabWurzelFuer(path);
  const m = findRoute(wurzel);
  if (!m) return path;
  // Ein Chat heißt wie sein Titel, nicht wie seine Kennung (docs/claude.md).
  if (m.route.path === "/claude/:id") {
    const id = m.params.id ?? "";
    if (id === "neu") return "Neuer Chat";
    return claudeChat(id)?.titel ?? "Chat";
  }
  // Eine Änderung heißt wie ihr Commit bzw. ihre Datei, nicht wie ihr Hash.
  if (m.route.path === "/claude/aenderung/:repo/:ziel") {
    const ziel = m.params.ziel ?? "";
    if (ziel.startsWith("datei:")) return ziel.slice(6).split("/").pop() || "Änderung";
    const rs = getClaude()?.stand.repo_staende?.find((r) => repoName(r.repo) === m.params.repo);
    return rs?.verlauf?.find((c) => c.hash.startsWith(ziel))?.titel ?? ziel.slice(0, 7);
  }
  const werte = Object.values(m.params ?? {});
  if (werte.length > 0) {
    const letzter = String(werte[werte.length - 1]);
    if (letzter) return decodeURIComponent(letzter);
  }
  return m.route.label;
}

/** registerAllPanels wird einmal beim App-Start gerufen. Idempotent,
 *  damit Hot-Reload im Dev keine Doppel-Registrierung ausloest. */
export function registerAllPanels(): void {
  if (registriert) return;
  registriert = true;

  // Eine Seite aus routes.ts. Der Typ, der aus der Navigation eine
  // Tab-Flaeche macht: jede Route ist ein Tab, das Dashboard genauso
  // wie /system/tasks. Identitaet ist der Pfad.
  registerPanel<{ path: string }>("page", {
    icon: (p) => seitenIcon(p.path),
    bereich: (p) => sectionForPath(p.path)?.id ?? null,
    title: (p) => seitenTitel(p.path),
    subtitle: (p) => {
      const sec = sectionForPath(p.path);
      return sec ? sec.label : "";
    },
    load: () => import("./PageHost.svelte"),
  });

  // Ein Dokument aus dem Vault in Lesegroesse.
  registerPanel<{ path: string; name?: string }>("vault-dokument", {
    icon: "file",
    bereich: "daten",
    title: (p) => p.name ?? p.path.split("/").pop() ?? p.path,
    subtitle: (p) => p.path,
    load: () => import("./panels/VaultDocumentPanel.svelte"),
  });

  // Fehlende Werte einer Entgeltabrechnung nachtragen. Gehoert NEBEN
  // das Beleg-Panel -- die beiden reden ueber den Auswahl-Kanal.
  registerPanel<{ path: string }>("entgelt-nachtragen", {
    icon: "pencil",
    bereich: "daten",
    title: () => "Nachtragen",
    subtitle: (p) => p.path,
    load: () => import("./panels/EntgeltNachtragenPanel.svelte"),
  });

  // Ein Docker-Container in der Tiefe.
  registerPanel<{ hostId: string; name: string }>("docker-container", {
    icon: "container",
    bereich: "infra",
    title: (p) => p.name,
    subtitle: (p) => p.hostId,
    load: () => import("./panels/DockerContainerPanel.svelte"),
  });
}
